import React, { FC, useEffect, useMemo, useState } from 'react';
import { Row, Select } from 'antd';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import { debounce } from 'lodash';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import { DashboardContainerColorProps } from 'enums/DashboardContainerEnums';
import { Organization } from 'models/CompanySetup/organization.model';
import DashboardContainer from 'views/Dashboard/DashboardContainer';
import styles from './company.module.scss';
import Header from './Header/header';
import { xAxisLabelContent } from './Barchart/xAxisLabelContent';

import { CompanyDashboardProps, getLatestYearFromMetadata, scopeData } from './constants';
import LocationBarChart from './LocationBarChart';
import CompanyDashboardEmptyState from './CompanyDashboardEmptyState';
import YoYEmissionChart from './YoYEmissionChart';
import ScopeDropdownAndPieChart from './ScopeDropdownAndPieChart';
import EmissionIntensityChart from "./EmissionIntensityChart";

// Services
import AccountsService from 'services/AccountsService/accounts.service';
import ParentCompanySetupService from 'services/ParentCompanySetup/parentCompanySetup.service';
import EmissionsService from 'services/ParentCompanySetup/emissions.service';
import MetadataService from 'services/ParentCompanySetup/metadata.service';
import Category4Service from 'services/Category4Service/category4.service';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { deserialize } from 'serializr';
import { OrganizationRevenue } from 'models/OrganizationRevenue/organizationRevenue.model';
import { LocationRevenue } from 'models/LocationRevenue/locationRevenue.model';

const { Option } = Select;

const CompanyDashboard: FC = () => {
  const { fetchCompany, company } = AccountsService();
  const { fetchParentOrganization, loading } = ParentCompanySetupService();
  const { belongsMetaData, fetchBelongsMetaData, yearsMetaDataByOrg, fetchYearsMetaDataByOrg,
    locMetaDataByOrg, fetchLocMetaDataByOrg, } = MetadataService();

  const { fetchEmissionData, emissionSourceChartData, fetchEmissionDataSource,
    emissionScopeChartData, fetchYoYEmissions, yoyData, locationData, pieloading,
    yearEmissionLoading, fetchEmissionByLocation, } = EmissionsService();

  const { organizationData } = useParentOrganizationContext();
  const [firstYear, setFirstYear] = useState<string>('');
  const [locationOption, setLocationOption] = useState<string>('');
  const [scopeValue, setScopeValue] = useState<string>(scopeData[0].value);
  const [parentOrg, setParentOrg] = useState<string>('');
  const [parentID, setParentID] = useState<number>();
  const [loadLine, setLoadLine] = useState<boolean>(false);
  const [isBaselineSet, setIsBaselineSet] = useState<boolean>(false);
  const [selectedTarget, setSelectedTarget] = useState<string>(CompanyDashboardProps.SELECT_TARGET);
  const [chartType, setChartType] = useState<string>('emissionByYear');

  // Intensity chart data states
  const [goodsData, setGoodsData] = useState<{ year: number; totalWeight: number }[]>([]);
  const [revenueData, setRevenueData] = useState<{
    year: number;
    revenue: number | null;
    revenueUnit: string | null;
    revenueStatus: boolean;
  }[]>([]);
  const [loadingWeights, setLoadingWeights] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);

  const category4Service = Category4Service();

  const fetchInitialData = () => {
    fetchBelongsMetaData();
    fetchParentOrganization();
    setLocationOption(CompanyDashboardProps.ALL);
  };

  const isScopeEmission = scopeValue === CompanyDashboardProps.ALL;
  const showStewwiButton = company?.isStewwiCustomer === true;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchOrganizationRelatedData = (organization: Organization) => {
    const orgId = organization?.id;
    if (orgId) {
      setParentID(orgId);
      setIsBaselineSet(true);
      fetchYearsMetaDataByOrg(orgId);
    }
  };
  useEffect(() => {
    if (!organizationData) return;
    const { organization } = organizationData;

    if (organization?.name && organization.name !== parentOrg) {
      setParentOrg(organization.name);
    }
    if (organization?.id && organization.id !== parentID) {
      fetchOrganizationRelatedData(organization);
    }
  }, [organizationData]);

  const callCommonAPIs = (
    year: number,
    parentId: number,
    locationOption?: number
  ) => {
    fetchYoYEmissions({
      organizationId: parentId,
      locationId: locationOption,
      year,
    });
    fetchEmissionByLocation({
      year,
      organizationId: parentId,
      locationId: locationOption,
    });
    fetchEmissionData({
      year,
      organizationId: parentId,
      locationId: locationOption,
    });
    fetchEmissionDataSource({
      year,
      organizationId: parentId,
      scopeCategory: isScopeEmission
        ? CompanyDashboardProps.SCOPE_1_VALUE
        : scopeValue,
      locationId: locationOption,
    });
  };

  useEffect(() => {
    if (!yearsMetaDataByOrg?.length || !parentID) return;
    const latestYear = getLatestYearFromMetadata(yearsMetaDataByOrg);
    if (!latestYear) return;
    const yearStr = String(latestYear);
    setFirstYear(yearStr);
    fetchLocMetaDataByOrg(parentID, latestYear);
    if (isBaselineSet) {
      callCommonAPIs(latestYear, parentID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsMetaDataByOrg, parentID, isBaselineSet]);

  const handleSubmit = () => {
    const year = Number(firstYear);
    setSelectedTarget(CompanyDashboardProps.SELECT_TARGET);
    if (
      locationOption !== CompanyDashboardProps.ALL &&
      locationOption !== undefined
    ) {
      callCommonAPIs(year, Number(parentID), Number(locationOption));
    } else {
      callCommonAPIs(year, Number(parentID));
    }
  };

  const handleOrgChange = async (val: string) => {
    const orgId = Number(val);
    setParentID(orgId);
    const filteredData = belongsMetaData.find(item => item.id === orgId);

    if (filteredData?.name) {
      setParentOrg(filteredData.name);
    }

    await fetchYearsMetaDataByOrg(orgId);

    setLocationOption(CompanyDashboardProps.ALL);

    setIsBaselineSet(false);
  };
  const handleLocationChange = (value: string) => {
    setLocationOption(value);
  };
  const handleChangeScope = (val: string) => {
    val !== CompanyDashboardProps.ALL &&
      fetchEmissionDataSource({
        year: Number(firstYear),
        organizationId: parentID,
        scopeCategory: val,
        ...(locationOption !== CompanyDashboardProps.ALL &&
          locationOption && { locationId: Number(locationOption) }),
      });
    setScopeValue(val);
  };

  const handleReset = () => {
    if (!organizationData) return;
    const { organization } = organizationData;
    const orgId = organization?.id;
    if (organization?.name) {
      setParentOrg(organization?.name);
    }
    setIsBaselineSet(true);
    if (orgId) {
      setParentID(orgId);
      fetchYearsMetaDataByOrg(orgId);
    }
    setLocationOption(CompanyDashboardProps.ALL);
  };

  const handleYearChange = debounce((value: string) => {
    setFirstYear(value);
    fetchLocMetaDataByOrg(Number(parentID), Number(value));
  }, 300);
  const handleChangeTarget = async (value?: string) => {
    if (!value) return;
    setLoadLine(true);
    setSelectedTarget(value);
    await fetchYoYEmissions({
      organizationId: Number(parentID),
      targetName: value,
      year: Number(firstYear),
      locationId:
        locationOption !== CompanyDashboardProps.ALL
          ? Number(locationOption)
          : undefined,
    });

    setLoadLine(false);
  };
  const handleSelectChange = (value?: string) => {
    if (value === undefined) {
      fetchYoYEmissions({
        organizationId: Number(parentID),
        year: Number(firstYear),
      });
      setSelectedTarget(CompanyDashboardProps.SELECT_TARGET);
    } else {
      handleChangeTarget(value);
    }
  };

  const paginatedYoYEmission = useMemo(
    () => yoyData?.yoyEmission,
    [yoyData?.yoyEmission]
  );

  const maxSbtiCO2e =
    yoyData?.yoyEmission?.reduce((max, entry) => {
      return Math.max(max, entry?.totalCO2e || 0);
    }, 0) || 0;

  // Fetch weight data when yoyData changes (after Apply is clicked)
  // Only fetch when yoyData changes, not when locationOption changes
  useEffect(() => {
    const fetchWeights = async () => {
      if (!yoyData?.yoyEmission || !parentID || locationOption === CompanyDashboardProps.ALL) {
        setGoodsData([]);
        return;
      }

      setLoadingWeights(true);
      const years = yoyData.yoyEmission
        .map((e) => e.year)
        .filter((year): year is number => year !== undefined);

      const results = await Promise.all(
        years.map(async (year) => {
          try {
            const res = await category4Service.getCategory4Data({
              locationId: String(locationOption),
              year: Number(year),
            });
            return {
              year,
              totalWeight: res?.finishedGoods?.totalWeight ?? 0,
            };
          } catch (err) {
            return { year, totalWeight: 0 };
          }
        })
      );

      setGoodsData(results);
      setLoadingWeights(false);
    };

    fetchWeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yoyData, parentID]); // Only depend on yoyData, not locationOption

  // Fetch revenue data when yoyData changes (after Apply is clicked)
  // Default currency is CAD - child component can refetch if currency changes
  useEffect(() => {
    const fetchRevenue = async () => {
      if (!yoyData?.yoyEmission || !parentID) {
        setRevenueData([]);
        return;
      }

      setLoadingRevenue(true);
      const years = yoyData.yoyEmission
        .map((e) => e.year)
        .filter((year): year is number => year !== undefined);

      // Organization-level revenue (rollup across all locations)
      if (locationOption === CompanyDashboardProps.ALL) {
        const results = await Promise.all(
          years.map(async (year) => {
            try {
              const response = await axiosInstance.get(ApiRoutes.GET_ORGANIZATION_REVENUE, {
                params: {
                  organization_id: parentID,
                  year,
                  currency: 'CAD', // Default currency
                },
              });
              const orgRevenue = deserialize(OrganizationRevenue, response.data) as OrganizationRevenue;
              const revenueRaw = orgRevenue?.revenue;
              const revenueComplete = orgRevenue?.revenueComplete ?? false;
              const returnedUnit = orgRevenue?.revenueUnit ?? 'CAD';

              const revenueValue =
                revenueRaw === null || revenueRaw === undefined
                  ? null
                  : typeof revenueRaw === 'string'
                    ? (isNaN(parseFloat(revenueRaw)) ? null : parseFloat(revenueRaw))
                    : Number(revenueRaw);

              return {
                year,
                revenue: revenueValue,
                revenueUnit: returnedUnit,
                revenueStatus: revenueComplete,
              };
            } catch (err) {
              return { year, revenue: null, revenueUnit: null, revenueStatus: false };
            }
          })
        );
        setRevenueData(results);
      } else {
        // Location-level revenue (single location)
        const results = await Promise.all(
          years.map(async (year) => {
            try {
              const response = await axiosInstance.get(ApiRoutes.GET_LOCATION_REVENUE, {
                params: {
                  location_id: String(locationOption),
                  year,
                  currency: 'CAD', // Default currency
                },
              });
              const locationRevenue = deserialize(LocationRevenue, response.data) as LocationRevenue;
              const revenueRaw = locationRevenue?.revenue;
              const revenueComplete = locationRevenue?.revenueStatus ?? false;
              const returnedUnit = locationRevenue?.revenueUnit ?? 'CAD';

              const revenueValue =
                revenueRaw === null || revenueRaw === undefined
                  ? null
                  : typeof revenueRaw === 'string'
                    ? (isNaN(parseFloat(revenueRaw)) ? null : parseFloat(revenueRaw))
                    : Number(revenueRaw);

              return {
                year,
                revenue: revenueValue,
                revenueUnit: returnedUnit,
                revenueStatus: revenueComplete,
              };
            } catch (err) {
              return { year, revenue: null, revenueUnit: null, revenueStatus: false };
            }
          })
        );
        setRevenueData(results);
      }

      setLoadingRevenue(false);
    };

    fetchRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yoyData, parentID]); // Only depend on yoyData, not locationOption

  if (!organizationData) return null;

  return (
    <DashboardContainer bgColor={DashboardContainerColorProps.GREY}>
      {!organizationData?.organization ? (
        <CompanyDashboardEmptyState />
      ) : (
        <div>
          <div className={styles.dashboardContainer}>
            <Header
              parentOrg={parentOrg}
              belongsMetaData={belongsMetaData}
              locMetaDataByOrg={locMetaDataByOrg}
              yearsMetaDataByOrg={yearsMetaDataByOrg}
              locationOption={locationOption}
              firstYear={firstYear}
              onOrgChange={handleOrgChange}
              onLocationChange={handleLocationChange}
              onYearChange={handleYearChange}
              onReset={handleReset}
              onSubmit={handleSubmit}
              loading={loading}
              yearEmissionLoading={yearEmissionLoading}
              pieLoading={pieloading}
              showStewwiButton={showStewwiButton}
            />
          </div>
          <div className={styles.dashboardCardContainer}>
            <div className={styles.cardContent}>
              <Row justify="space-around" className={styles.graphsWrapper}>
                <Row className={styles.emissionChartDropdownRow}>
                  <div className={styles.emissionChartTypeDropdownWrap}>
                    <Select
                      value={chartType}
                      onChange={(value: string) => setChartType(value)}
                      className={styles.emissionChartDropdown}
                      suffixIcon={<Arrow fontSize={44} />}
                    >
                      <Option value="emissionByYear">
                        {CompanyDashboardProps.YOY_TEXT}
                      </Option>
                      <Option value="emissionIntensity">
                        {CompanyDashboardProps.INTENSITY_TEXT}
                      </Option>
                    </Select>
                  </div>
                </Row>

                {chartType === 'emissionByYear' ? (
                  <YoYEmissionChart
                    yoyData={yoyData}
                    selectedTarget={selectedTarget}
                    handleSelectChange={handleSelectChange}
                    loadLine={loadLine}
                    yearEmissionLoading={yearEmissionLoading}
                    paginatedYoYEmission={paginatedYoYEmission}
                    locationOption={locationOption}
                    maxSbtiCO2e={maxSbtiCO2e} // no
                    firstYear={firstYear}
                    fetchYoYEmissions={fetchYoYEmissions}
                    parentID={Number(parentID)}
                  />
                ) : (
                  <EmissionIntensityChart
                    yoyData={yoyData}
                    yearEmissionLoading={yearEmissionLoading}
                    parentID={Number(parentID)}
                    locationOption={locationOption}
                    loadLine={loadLine}
                    goodsData={goodsData}
                    revenueData={revenueData}
                    loadingWeights={loadingWeights}
                    loadingRevenue={loadingRevenue}
                  />
                )}


                <ScopeDropdownAndPieChart
                  scopeValue={scopeValue}
                  handleChangeScope={handleChangeScope}
                  scopeData={scopeData}
                  pieloading={pieloading}
                  firstYear={firstYear}
                  parentID={Number(parentID)}
                  locationOption={locationOption}
                  isScopeEmission={isScopeEmission}
                  fetchEmissionData={fetchEmissionData}
                  fetchEmissionDataSource={fetchEmissionDataSource}
                  emissionSourceChartData={emissionSourceChartData}
                  emissionScopeChartData={emissionScopeChartData}
                />
              </Row>
              <LocationBarChart
                loading={loading}
                locationData={locationData}
                locationOption={locationOption}
                fetchEmissionByLocation={fetchEmissionByLocation}
                firstYear={firstYear}
                parentID={Number(parentID)}
                xAxisLabelContent={xAxisLabelContent}
              />
            </div>
          </div>
        </div>
      )}
    </DashboardContainer>
  );
};

export default CompanyDashboard;
